/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:19:14 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ListIcon from "@mui/icons-material/List";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import { useOSAPIFormContext } from "ui-widgets/forms/providers";
import { ListEditorProvider, useListEditorContext } from "lib/providers";
import { FormField, ChoiceOptionGroup, ChoiceOption, Organization } from "lib/schemas";
import { getDefaultChoiceOption } from "lib/factories";
import {
  useAPIListChoiceOptionGroups,
  useAPIFetchChoiceOptionGroup,
  useAPIMutateChoiceOption,
  useAPIMutateChoiceOptionGroup,
  useAPIDeleteChoiceOption,
} from "form-builder/services";
import { APIItemDisplayWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import {
  APIListProgressWrapper,
  EmphasisText,
  FormOptionsSeparator,
  HelperText,
  OSActionButton,
  StyledOSDialog,
} from "ui-widgets";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { APISERVICEPATHS } from "lib/api";
import { v4 as uuid4 } from "uuid";
import { PrimaryKeyValue } from "lib/types";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { COLORSCHEME } from "theme/styles";
import { TranslatableInputWrapper } from "translation/components";

export const ChoiceOptionGroupComponent = () => {
  const {
    useFormReturn: { setValue, watch },
    submitAPIForm,
  } = useOSAPIFormContext<FormField>();

  const optionGroupId = watch<keyof FormField>("option_group");

  const formId = watch<keyof FormField>("form") as PrimaryKeyValue<FormField>;

  const fieldLabel = watch<keyof FormField>("label");

  const organizationId = watch<keyof FormField>("artifact_org") as PrimaryKeyValue<Organization>;

  const {
    fetchItem: fetchOptionGroup,
    apiItem: fetchedChoiceOptionGroup,
    isAPIError,
    isMakingAPIRequest,
  } = useAPIFetchChoiceOptionGroup();

  React.useEffect(() => {
    if (optionGroupId) fetchOptionGroup(optionGroupId);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionGroupId]);

  const [mutatedOptionGroup, setMutatedOptionGroup] = React.useState<ChoiceOptionGroup>();

  const { postData, patchData } = useAPIMutateChoiceOptionGroup();

  const createNewOptionGroup = (optionGroup: ChoiceOptionGroup) =>
    postData({ body: JSON.stringify(optionGroup), setValidAPIResponse: setMutatedOptionGroup });

  const updateOptionGroup = (optionGroupData: Partial<ChoiceOptionGroup>) =>
    patchData({
      apiPathParams: [optionGroupId],
      body: JSON.stringify(optionGroupData),
      setValidAPIResponse: setMutatedOptionGroup,
    });

  React.useEffect(() => {
    if (mutatedOptionGroup?.id && !optionGroupId) {
      // Set the option_group id, ideally it should never change again, user will just edit options here.
      setValue("option_group", mutatedOptionGroup.id);
      submitAPIForm();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutatedOptionGroup?.id]);

  const handleOnBlurTitle = (event: React.FocusEvent<HTMLInputElement>) => {
    const name = event.target.value;

    if (!fetchedChoiceOptionGroup) {
      createNewOptionGroup({
        name,
        artifact_org: organizationId,
        is_global: false,
        identifier: uuid4(),
      });
      return;
    }

    updateOptionGroup({ name });
  };

  const autoCreateOptionGroup = () => {
    createNewOptionGroup({
      name: `${fieldLabel} Options`,
      form: formId,
      artifact_org: organizationId,
      is_global: false,
      identifier: uuid4(),
    });
  };

  const autoCreateOnAddNewOption = () => {
    if (!fetchedChoiceOptionGroup) autoCreateOptionGroup();
  };

  const onCreateFreshOptionGroup = () => {
    setValue("option_group", null);
    autoCreateOptionGroup();
  };

  return (
    <APIItemDisplayWrapper showLoader={isMakingAPIRequest} loaderType="linear" isAPIError={isAPIError}>
      <Stack direction="column" spacing={2}>
        <TextField
          label="Options Title"
          defaultValue={fetchedChoiceOptionGroup?.name}
          onBlur={handleOnBlurTitle}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => event.stopPropagation()}
        />
        <ChoiceOptionsListEditor
          autoCreateOnAddNewOption={autoCreateOnAddNewOption}
          optionGroup={fetchedChoiceOptionGroup}
        />
        <div style={{ padding: "16px 0" }}>
          <FormOptionsSeparator seperatorText="OR" />
        </div>
        <ChooseExistingOptionGroup
          onChooseGroup={(optionGroup) => setValue("option_group", optionGroup.id, { shouldDirty: true })}
        />
        <HelperText>
          If you want to re-use previously created options, click the button above.{" "}
          <EmphasisText color={COLORSCHEME.warning}>
            Be careful when editing options as existing options are most likely also used in other forms
          </EmphasisText>
        </HelperText>
        {fetchedChoiceOptionGroup && (
          <>
            <div style={{ padding: "16px 0" }}>
              <FormOptionsSeparator seperatorText="OR" />
            </div>
            <OSActionButton actionStyle="add" onClick={onCreateFreshOptionGroup}>
              Create a fresh set of options
            </OSActionButton>
          </>
        )}
      </Stack>
    </APIItemDisplayWrapper>
  );
};

interface ChoiceOptionsListEditorProps {
  optionGroup: ChoiceOptionGroup;
  autoCreateOnAddNewOption?: () => void;
}

export const ChoiceOptionsListEditor = ({ optionGroup, autoCreateOnAddNewOption }: ChoiceOptionsListEditorProps) => {
  return (
    <ListEditorProvider<ChoiceOption, false>
      uniqueIdentifier="id"
      apiServicePath={APISERVICEPATHS.fieldChoiceOption}
      apiQueryParams={{ option_group: optionGroup?.id }}
      makeRequestOnInitialMount={Boolean(optionGroup?.id)}
    >
      <ChoiceOptionsList autoCreateOnAddNewOption={autoCreateOnAddNewOption} optionGroup={optionGroup} />
    </ListEditorProvider>
  );
};

interface ChoiceOptionsListProps {
  optionGroup: ChoiceOptionGroup;
  autoCreateOnAddNewOption?: () => void;
}

export const ChoiceOptionsList = ({ optionGroup, autoCreateOnAddNewOption }: ChoiceOptionsListProps) => {
  const {
    createNewItem,
    itemsListState,
    selectItem,
    currentMutationItem,
    updateList,
    removeItem,
    creationQueueHasItems,
    endOfListPosition,
  } = useListEditorContext<ChoiceOption>();

  const { savedItems: choiceOptions, itemsLoaded } = itemsListState;

  const createNewOption = React.useCallback(
    () => createNewItem(getDefaultChoiceOption(optionGroup, "label", "value", endOfListPosition)),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [optionGroup, endOfListPosition]
  );

  const handleAddClick = () => {
    if (!optionGroup) {
      autoCreateOnAddNewOption?.();
      return;
    }
    createNewOption();
  };

  React.useEffect(() => {
    if (optionGroup?.id && itemsLoaded && choiceOptions?.length === 0) createNewOption();
  }, [createNewOption, optionGroup?.id, choiceOptions?.length, itemsLoaded, endOfListPosition]);

  const [mutatedChoiceOption, setMutatedChoiceOption] = React.useState<ChoiceOption>(null);

  const { postData, putData } = useAPIMutateChoiceOption();

  const postNewChoiceOption = (choiceOption: ChoiceOption) =>
    postData({ body: JSON.stringify(choiceOption), setValidAPIResponse: setMutatedChoiceOption });

  const putChoiceOption = (choiceOption: ChoiceOption) => {
    choiceOption?.id &&
      putData({
        apiPathParams: [choiceOption.id],
        body: JSON.stringify(choiceOption),
        setValidAPIResponse: setMutatedChoiceOption,
      });
  };

  React.useEffect(() => {
    if (!currentMutationItem) return;

    if (creationQueueHasItems) {
      postNewChoiceOption(currentMutationItem);
      return;
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentMutationItem), creationQueueHasItems]);

  React.useEffect(() => {
    mutatedChoiceOption?.id && updateList(mutatedChoiceOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(mutatedChoiceOption)]);

  return (
    <Grid container rowSpacing={2}>
      <Grid item md={12}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {choiceOptions.map((choiceOption) => (
                <TableRow key={`${choiceOption.id}`} onClick={() => selectItem(choiceOption.id)}>
                  <TableCell>
                    <TranslatableInputWrapper<ChoiceOption>
                      fieldKey="option_label"
                      fieldValue={choiceOption.option_label}
                      getTranslatedValue={(translatedFieldKey) => choiceOption[translatedFieldKey]}
                      setTranslatedValue={(translatedFieldKey, translatedValue) =>
                        putChoiceOption({ ...choiceOption, [translatedFieldKey]: translatedValue })
                      }
                    >
                      <TextField
                        defaultValue={choiceOption.option_label}
                        onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                          putChoiceOption({ ...choiceOption, option_label: event.target.value })
                        }
                      />
                    </TranslatableInputWrapper>
                  </TableCell>
                  <TableCell>
                    <TextField
                      defaultValue={choiceOption.option_value}
                      onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                        putChoiceOption({ ...choiceOption, option_value: event.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <DeleteOSItemDialog<ChoiceOption>
                      itemId={choiceOption.id}
                      useDeleteOSItem={useAPIDeleteChoiceOption}
                      onDelete={() => removeItem(choiceOption.id)}
                      onRestore={(restoredChoiceOption) => updateList(restoredChoiceOption)}
                      dialogTitle="Delete this option?"
                      dialogMessage="Deleting an option will affect how entries already saved with this value are displayed. Are you sure about this?"
                    >
                      {({ handleOpen }) => (
                        <IconButton onClick={handleOpen}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      )}
                    </DeleteOSItemDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item md={11}></Grid>
      <Grid item md={1}>
        <OSActionButton actionStyle="add" buttonType="floating" onClick={handleAddClick} />
      </Grid>
    </Grid>
  );
};

type ChooseExistingOptionGroupProps = {
  onChooseGroup: (optionGroup: ChoiceOptionGroup) => void;
};

const ChooseExistingOptionGroup = ({ onChooseGroup }: ChooseExistingOptionGroupProps) => {
  const {
    items: optionGroups,
    isAPIError,
    httpResponseStatusCode,
    isMakingAPIRequest,
    fetchList,
  } = useAPIListChoiceOptionGroups();
  return (
    <StyledOSDialog
      popUpIcon={<ListIcon color="primary" />}
      popUpTitle={<>Choose existing Options</>}
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton
          actionStyle="custom"
          customIcon={<ListIcon />}
          onClick={() => {
            fetchList();
            handleOpen();
          }}
        >
          Choose Existing Options
        </OSActionButton>
      )}
      renderContentWithClose={({ handleClose }) => (
        <APIListProgressWrapper
          isAPIError={isAPIError}
          items={optionGroups}
          httpResponseStatusCode={httpResponseStatusCode}
          showLoader={isMakingAPIRequest}
        >
          <List component="nav" aria-label="option-groups-list">
            {optionGroups.map((optionGroup) => (
              <ListItemButton key={optionGroup.id}>
                <Grid container>
                  <Grid item md={11}>
                    <ListItemText primary={optionGroup.name} />
                  </Grid>
                  <Grid
                    item
                    md={1}
                    onClick={(e) => {
                      onChooseGroup(optionGroup);
                      handleClose();
                    }}
                  >
                    <AddCircleIcon color="primary" />
                  </Grid>
                </Grid>
              </ListItemButton>
            ))}
          </List>
        </APIListProgressWrapper>
      )}
    />
  );
};
